import React from 'react';
import Button from '../../../../../components/Atoms/Button.jsx';
import { useI18n } from '../../../../../i18n';

/**
 * "Pre-label with your own trained model" — the model-in-the-loop step.
 *
 * Only rendered once at least one version of this model has finished training,
 * because before that there is nothing to predict with. Suggestions arrive as
 * the same dashed, editable AI boxes the rest of this panel produces, so the
 * accept / discard controls below cover them too.
 *
 */

const CONF_MIN = 0.05;
const CONF_MAX = 0.95;

const clampTo = (value, min, max) => Math.min(max, Math.max(min, value));

/** Best available headline metric, whatever the model type recorded. */
const metricFor = (version) => {
    const metrics = version?.performanceMetrics || {};
    const map = metrics['metrics/mAP50(B)'] ?? metrics.mAP50 ?? metrics.mAP;
    if (typeof map === 'number') return `mAP50 ${(map * 100).toFixed(1)}%`;
    const acc = metrics.top1_acc ?? metrics.accuracy;
    if (typeof acc === 'number') return `acc ${(acc * 100).toFixed(1)}%`;
    return null;
};

const versionLabel = (version) => {
    const metric = metricFor(version);
    return metric ? `${version.version} · ${metric}` : String(version.version ?? version._id);
};

const PreLabelSection = ({
    trainedVersions,
    selectedVersionId,
    setVersionId,
    confidence,
    setConfidence,
    isRunning,
    isStopping,
    progress,
    error,
    clearError,
    unlabeledCount,
    preLabelCurrent,
    preLabelUnlabeled,
    stop,
    disabled,
}) => {
    const { t } = useI18n();
    if (!trainedVersions || trainedVersions.length === 0) return null;

    const busy = isRunning || isStopping;
    const blocked = busy || disabled;

    const onConfidence = (raw) => {
        const value = Number(raw);
        if (Number.isFinite(value)) setConfidence(clampTo(value, CONF_MIN, CONF_MAX));
    };

    return (
        <div className="auto-label-panel__prelabel">
            <div className="auto-label-panel__section-title">
                <i className="fa fa-sync-alt" aria-hidden="true"></i> {t('aiLabel.preLabel.title')}
            </div>
            <small className="auto-label-panel__hint">
                {t('aiLabel.preLabel.hint')}
            </small>

            <label className="auto-label-panel__label mt-2" htmlFor="prelabel-version">
                {t('aiLabel.preLabel.version')}
            </label>
            <select
                id="prelabel-version"
                className="form-select form-select-sm auto-label-panel__select"
                value={selectedVersionId}
                onChange={(e) => { clearError?.(); setVersionId(e.target.value); }}
                disabled={blocked}
            >
                {trainedVersions.map((version) => (
                    <option key={version._id} value={version._id}>{versionLabel(version)}</option>
                ))}
            </select>

            <label className="auto-label-panel__label mt-2" htmlFor="prelabel-confidence">
                {t('aiLabel.preLabel.confidence')}
            </label>
            <div className="auto-label-panel__threshold">
                <input
                    id="prelabel-confidence"
                    type="range"
                    className="form-range"
                    min={CONF_MIN}
                    max={CONF_MAX}
                    step="0.01"
                    value={confidence}
                    onChange={(e) => onConfidence(e.target.value)}
                />
                <input
                    type="number"
                    className="form-control form-control-sm auto-label-panel__number"
                    min={CONF_MIN}
                    max={CONF_MAX}
                    step="0.01"
                    value={confidence}
                    onChange={(e) => onConfidence(e.target.value)}
                    aria-label={t('aiLabel.preLabel.confidence')}
                />
            </div>

            {busy ? (
                <div className="auto-label-panel__progress mt-2">
                    {progress && (
                        <div className="progress" style={{ height: '6px' }}>
                            <div
                                className="progress-bar"
                                style={{ width: `${Math.round((progress.done / Math.max(1, progress.total)) * 100)}%` }}
                            />
                        </div>
                    )}
                    <small className="auto-label-panel__hint">
                        {progress
                            ? t('aiLabel.preLabel.progress', {
                                done: progress.done,
                                total: progress.total,
                                found: progress.found,
                            })
                            : t('aiLabel.preLabel.running')}
                    </small>
                    <Button
                        variant="outline-danger"
                        size="sm"
                        className="w-100 mt-1"
                        onClick={stop}
                        disabled={isStopping}
                        tooltipText={t('aiLabel.preLabel.stopTip')}
                    >
                        <i className="fa fa-stop" aria-hidden="true"></i>{' '}
                        {isStopping ? t('aiLabel.preLabel.stopping') : t('aiLabel.preLabel.stop')}
                    </Button>
                </div>
            ) : (
                <div className="auto-label-panel__actions d-flex gap-1 mt-2">
                    <Button
                        variant="outline-primary"
                        size="sm"
                        className="flex-fill"
                        onClick={preLabelCurrent}
                        disabled={blocked || !selectedVersionId}
                        tooltipText={t('aiLabel.preLabel.currentTip')}
                    >
                        <i className="fa fa-magic" aria-hidden="true"></i> {t('aiLabel.preLabel.current')}
                    </Button>
                    <Button
                        variant="primary"
                        size="sm"
                        className="flex-fill"
                        onClick={preLabelUnlabeled}
                        disabled={blocked || !selectedVersionId || unlabeledCount === 0}
                        tooltipText={t('aiLabel.preLabel.unlabeledTip')}
                    >
                        <i className="fa fa-layer-group" aria-hidden="true"></i>{' '}
                        {t('aiLabel.preLabel.unlabeled', { count: unlabeledCount })}
                    </Button>
                </div>
            )}

            {error && !busy && (
                <small className="auto-label-panel__error">
                    {error === 'noDetections'
                        ? t('aiLabel.preLabel.noDetections')
                        : t('aiLabel.preLabel.failed', { reason: error })}
                </small>
            )}
        </div>
    );
};

export default PreLabelSection;
